import { FC } from 'react';

import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { IRaffleForm, validationSchema } from '../form';

import css from './RaffleFormDialog.module.css';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';

interface RaffleFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: IRaffleForm;
  isSubmitting: boolean;
  onSubmit: (values: IRaffleForm, resetForm: VoidFunction) => void;
}

export const RaffleFormDialog: FC<RaffleFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<IRaffleForm>({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
  });

  const handleSelectImage = (fileId: string) => {
    formik.setFieldValue('rewardImageId', fileId);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="raffleForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="name"
            value={formik.values.name}
            error={!!formik.errors.name}
            helperText={formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            required
          />

          <TextField
            name="participantsMaxCount"
            value={formik.values.participantsMaxCount}
            error={!!formik.errors.participantsMaxCount}
            helperText={formik.errors.participantsMaxCount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Max Participants"
            type="number"
            required
          />

          <div className={css.raffleFormGroup}>
            <TextField
              name="rewardValue"
              value={formik.values.rewardValue}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Reward Value"
              required
            />

            <FileSelector
              value={formik.values.rewardImageId}
              onSelect={handleSelectImage}
              error={formik.errors.rewardImageId}
              label="Reward Image"
              required
            />
          </div>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="raffleForm" loading={isSubmitting} type="submit" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
